<template>
  <div class="action-toolbar" :class="{ 'action-toolbar--split': isTransitioning }">

    <!-- Primary advance zone — splits when isTransitioning -->
    <template v-if="statusActions.next.length > 0 && isOperatorOrAbove">
      <button
        v-if="!isTransitioning"
        class="toolbar-btn toolbar-btn--primary"
        :disabled="advancingStatus"
        @click="$emit('advance-status', statusActions.next[0].status)"
      >
        <svg class="toolbar-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
        {{ statusActions.next[0].label }}
      </button>

      <template v-else>
        <button
          v-for="action in statusActions.next"
          :key="action.status"
          class="toolbar-btn toolbar-btn--primary toolbar-btn--split-half"
          :disabled="advancingStatus"
          @click="$emit('advance-status', action.status)"
        >
          <svg class="toolbar-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
          {{ action.label }}
        </button>
      </template>
    </template>

    <!-- Planned Items -->
    <button
      class="toolbar-btn toolbar-btn--ghost"
      @click="router.push(`/project/${projectId}/items`)"
    >
      <svg class="toolbar-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
        <rect x="9" y="3" width="6" height="4" rx="1"/>
        <polyline points="9 12 11 14 15 10"/>
      </svg>
      {{ $t('projects.planned') }}
    </button>

    <!-- Checked Out Items -->
    <button
      class="toolbar-btn toolbar-btn--ghost"
      @click="router.push(`/checkouts?projectId=${projectId}`)"
    >
      <svg class="toolbar-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="20 12 20 22 4 22 4 12"/>
        <rect x="2" y="7" width="20" height="5"/>
        <line x1="12" y1="22" x2="12" y2="7"/>
      </svg>
      {{ $t('projects.checkedOut') }}
    </button>

    <!-- Person Checkouts -->
    <button
      class="toolbar-btn toolbar-btn--ghost"
      @click="router.push(`/project/${projectId}/person-checkouts`)"
    >
      <svg class="toolbar-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 00-3-3.87"/>
        <path d="M16 3.13a4 4 0 010 7.75"/>
      </svg>
      {{ $t('projects.personCheckouts') }}
    </button>

    <!-- Check In All -->
    <button
      v-if="isOperatorOrAbove"
      class="toolbar-btn toolbar-btn--checkin"
      :disabled="checkingInAll"
      @click="$emit('check-in-all')"
    >
      <svg class="toolbar-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
        <polyline points="20 6 9 17 4 12"/>
      </svg>
      {{ checkingInAll ? $t('projects.checkingInAll') : $t('projects.checkInAll') }}
    </button>

    <!-- Cancel project -->
    <template v-if="statusActions.cancel && isOperatorOrAbove">
      <!-- Step 1: trigger button -->
      <button
        v-if="!cancelPending"
        class="toolbar-btn toolbar-btn--cancel"
        :disabled="advancingStatus"
        @click="cancelPending = true"
        :title="$t('projects.cancelProject')"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>

      <!-- Step 2: confirm row -->
      <div v-else class="cancel-confirm">
        <span class="cancel-confirm__label">{{ $t('projects.cancelProjectConfirm') }}</span>
        <button
          class="cancel-confirm__btn cancel-confirm__btn--yes"
          :disabled="advancingStatus"
          @click="$emit('advance-status', 8); cancelPending = false"
        >
          {{ $t('projects.yesCancel') }}
        </button>
        <button
          class="cancel-confirm__btn cancel-confirm__btn--no"
          @click="cancelPending = false"
        >
          {{ $t('projects.keepProject') }}
        </button>
      </div>
    </template>

  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { usePermissions } from '@/composables/usePermissions.js'

const props = defineProps({
  statusActions: {
    type: Object,
    default: () => ({ next: [], cancel: false }),
  },
  advancingStatus: Boolean,
  checkingInAll:   Boolean,
  projectId:       [String, Number],
})

defineEmits(['advance-status', 'check-in-all'])

const router = useRouter()
const cancelPending = ref(false)
const { isOperatorOrAbove } = usePermissions()

const isTransitioning = computed(() => props.statusActions.next.length > 1)
</script>

<style scoped>
/* ── Toolbar container ─────────────────────────────────────── */
.action-toolbar {
  display: flex;
  flex-wrap: nowrap;
  gap: .5rem;
  align-items: stretch;
}

/* ── Base button ──────────────────────────────────────────── */
.toolbar-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: .375rem;
  height: 44px;
  padding: 0 .875rem;
  border-radius: .625rem;
  border: 1px solid transparent;
  font-size: .8rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background .15s, border-color .15s, opacity .15s;
  flex: 1;
  min-width: 0;
}
.toolbar-btn:disabled { opacity: .45; cursor: not-allowed; }

.toolbar-btn__icon { width: 15px; height: 15px; flex-shrink: 0; }

/* ── Primary (Mark Ready / status advance) ────────────────── */
.toolbar-btn--primary {
  background: #059669;
  border-color: #059669;
  color: #fff;
}
.toolbar-btn--primary:hover:not(:disabled) {
  background: #047857;
  border-color: #047857;
}
.toolbar-btn--split-half { flex: 1; }

/* ── Ghost / outlined ─────────────────────────────────────── */
.toolbar-btn--ghost {
  background: #0f172a;
  border-color: #1e293b;
  color: #94a3b8;
}
.toolbar-btn--ghost:hover:not(:disabled) {
  background: #1e293b;
  border-color: #475569;
  color: #f1f5f9;
}

/* ── Check In All ─────────────────────────────────────────── */
.toolbar-btn--checkin {
  background: #0d1f16;
  border-color: #166534;
  color: #4ade80;
}
.toolbar-btn--checkin:hover:not(:disabled) {
  background: #14532d;
  border-color: #16a34a;
}

/* ── Cancel trigger (icon-only, fixed width) ─────────────── */
.toolbar-btn--cancel {
  flex: 0 0 44px;
  min-width: 44px;
  padding: 0;
  background: transparent;
  border-color: #3f1515;
  color: #f87171;
}
.toolbar-btn--cancel:hover:not(:disabled) {
  background: #1f0a0a;
  border-color: #7f1d1d;
}
.toolbar-btn--cancel svg { width: 16px; height: 16px; }

/* ── Cancel confirm row ───────────────────────────────────── */
.cancel-confirm {
  display: flex;
  align-items: center;
  gap: .5rem;
  flex: 1;
  background: #1a0a0a;
  border: 1px solid #7f1d1d;
  border-radius: .625rem;
  padding: 0 .75rem;
  height: 44px;
  min-width: 0;
}
.cancel-confirm__label {
  flex: 1;
  font-size: .8rem;
  font-weight: 600;
  color: #fca5a5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cancel-confirm__btn {
  flex-shrink: 0;
  height: 28px;
  padding: 0 .625rem;
  border-radius: .375rem;
  border: 1px solid transparent;
  font-size: .75rem;
  font-weight: 700;
  cursor: pointer;
  transition: background .15s, border-color .15s;
}
.cancel-confirm__btn:disabled { opacity: .45; cursor: not-allowed; }
.cancel-confirm__btn--yes {
  background: #7f1d1d;
  border-color: #b91c1c;
  color: #fee2e2;
}
.cancel-confirm__btn--yes:hover:not(:disabled) {
  background: #991b1b;
}
.cancel-confirm__btn--no {
  background: #1f2937;
  border-color: #374151;
  color: #9ca3af;
}
.cancel-confirm__btn--no:hover:not(:disabled) {
  background: #374151;
  color: #f1f5f9;
}

/* ── Mobile: 2×2 grid ─────────────────────────────────────── */
@media (max-width: 640px) {
  .action-toolbar {
    flex-wrap: wrap;
  }

  /* Each button takes ≈50% → 2 per row */
  .toolbar-btn {
    flex: 1 0 calc(50% - .25rem);
    min-width: calc(50% - .25rem);
  }

  /* Cancel trigger stays icon-sized; confirm row spans full width */
  .toolbar-btn--cancel {
    flex: 0 0 44px;
    min-width: 44px;
  }
  .cancel-confirm {
    flex: 1 0 100%;
  }

  /* In split mode the two primary halves already each take 50%,
     so no override needed. */
}
</style>
